import { useCallback, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Button, PageContainer, Skeleton, StatusMessage } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { AlertTriangle, ArrowLeft, Trash2, UserPlus, Users } from 'lucide-react';

import Can from '../components/guards/Can';
import {
  AddMembersModal,
  DeleteGroupModal,
  GroupHeaderCard,
  GroupMembersTable,
  RemoveFromGroupButton,
} from '../components/groups';
import { GROUP_PERMISSIONS } from '../constants/groupPermissions';
import { PERMISSIONS } from '../constants/permissions';
import { useGroup } from '../hooks/useGroup';
import { useGroupMemberRemoval } from '../hooks/useGroupMemberRemoval';
import { useGroupMutations } from '../hooks/useGroupMutations';
import { usePermission } from '../hooks/usePermission';
import { getBackTo, getGroupsListPath, getMemberDetailPath } from '../utils/groupPaths';

const stopRowClick = (event) => event.stopPropagation();

/**
 * Group detail / manage: header card (inline name and description edit, member count, Delete
 * group), the members table and the Add members modal. Full page, outside the tab layout.
 */
function GroupDetailPage() {
  const { t } = useTranslation('people-access');
  const { groupId } = useParams();
  const { pathname, state: linkState } = useLocation();
  // Whoever linked here (a member's Groups tab) can set `state.backTo`; Back returns there.
  const backTo = getBackTo(linkState);
  const navigate = useNavigate();
  // Members rows only open the member detail for who may view members (its route is guarded).
  const { allowed: canViewMembers } = usePermission(PERMISSIONS.MEMBERS_VIEW);
  const { group, members, isLoading, error, notFound, reload, applyGroup, hideMember, showMember, dropMember } =
    useGroup(groupId);
  const { removeMember } = useGroupMemberRemoval({
    groupId,
    hideMember,
    showMember,
    dropMember,
  });
  const { updateGroup, addMembers, deleteGroup } = useGroupMutations();
  const [isAdding, setIsAdding] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const openMember = useCallback(
    (member) => navigate(getMemberDetailPath(pathname, member.id), { state: { backTo: pathname } }),
    [navigate, pathname],
  );

  const goToList = useCallback(
    () => navigate(backTo ?? getGroupsListPath(pathname)),
    [navigate, pathname, backTo],
  );

  const handleRename = useCallback(
    async (name) => applyGroup(await updateGroup(groupId, { name })),
    [applyGroup, updateGroup, groupId],
  );
  const handleRedescribe = useCallback(
    async (description) => applyGroup(await updateGroup(groupId, { description })),
    [applyGroup, updateGroup, groupId],
  );
  const handleAdd = useCallback(
    async (memberIds) => {
      await addMembers(groupId, memberIds);
      reload();
    },
    [addMembers, groupId, reload],
  );
  const handleDelete = useCallback(async () => {
    await deleteGroup(groupId);
    goToList();
  }, [deleteGroup, groupId, goToList]);

  const handleOpenAdd = useCallback(() => setIsAdding(true), []);
  const handleCloseAdd = useCallback(() => setIsAdding(false), []);
  const handleOpenDelete = useCallback(() => setIsDeleting(true), []);
  const handleCloseDelete = useCallback(() => setIsDeleting(false), []);

  const deleteAction = useMemo(
    () => (
      <Can permission={GROUP_PERMISSIONS.DELETE}>
        <Button
          variant="ghost"
          size="sm"
          leftIcon={Trash2}
          aria-label={t('groups.detail.deleteGroup')}
          onClick={handleOpenDelete}
        />
      </Can>
    ),
    [t, handleOpenDelete],
  );

  const renderRowActions = useCallback(
    (member) => (
      // A click on the actions never opens the member row, not even on a locked (disabled) control.
      <div role="presentation" className="contents" onClick={stopRowClick}>
        <Can permission={GROUP_PERMISSIONS.MANAGE}>
          <RemoveFromGroupButton member={member} onRemove={removeMember} />
        </Can>
      </div>
    ),
    [removeMember],
  );

  const renderBody = () => {
    if (isLoading && !group) {
      return (
        <div data-testid="group-loading" className="flex flex-col gap-4">
          <Skeleton height="112px" borderRadius="8px" />
          <Skeleton height="160px" borderRadius="8px" />
        </div>
      );
    }
    if (notFound) {
      return (
        <StatusMessage
          icon={Users}
          title={t('groups.detail.notFoundTitle')}
          subtitle={t('groups.detail.notFoundSubtitle')}
        />
      );
    }
    if (error || !group) {
      return (
        <div className="flex flex-col items-center gap-3">
          <StatusMessage
            icon={AlertTriangle}
            title={error ?? t('groups.detail.loadError')}
            subtitle=""
          />
          <Button variant="secondary" size="sm" onClick={reload}>
            {t('groups.list.retry')}
          </Button>
        </div>
      );
    }
    return (
      <>
        <GroupHeaderCard
          group={group}
          onRename={handleRename}
          onRedescribe={handleRedescribe}
          actions={deleteAction}
        />
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-content">
            {t('groups.detail.membersHeading', { count: group.memberCount })}
          </h3>
          <Can permission={GROUP_PERMISSIONS.MANAGE}>
            <Button variant="tertiary" size="sm" leftIcon={UserPlus} onClick={handleOpenAdd}>
              {t('groups.detail.addMembers')}
            </Button>
          </Can>
        </div>
        {/* Row navigation is not a control: without members view it is simply not clickable, in hide and lock alike. */}
        <GroupMembersTable
          members={members}
          loading={isLoading}
          onRowClick={canViewMembers ? openMember : undefined}
          renderRowActions={renderRowActions}
        />
        <AddMembersModal
          isOpen={isAdding}
          groupId={group.id}
          groupName={group.name}
          onClose={handleCloseAdd}
          onAdd={handleAdd}
        />
        <DeleteGroupModal
          isOpen={isDeleting}
          groupName={group.name}
          onClose={handleCloseDelete}
          onConfirm={handleDelete}
        />
      </>
    );
  };

  return (
    <PageContainer>
      <div data-testid="page-group-detail" className="flex flex-col gap-6">
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            leftIcon={ArrowLeft}
            aria-label={t('groups.detail.back')}
            onClick={goToList}
          />
          <h1 className="text-xl font-bold text-content">{t('app.title')}</h1>
        </div>
        {renderBody()}
      </div>
    </PageContainer>
  );
}

export default GroupDetailPage;
